import React, { useEffect, useState } from 'react';
import { t } from '../../i18n/i18n';
import './QuestionText.scss';
import GenericToggle from '../GenericToggle/GenericToggle';
import HorizontalLoader from '../HorizontalLoader/HorizontalLoader';
import {
  AnswerTypeEnum,
  MECHANOGRAFY_PPM_TEST,
  PlanFeaturesIds,
  PromptSubTypes,
  TestFileType,
} from '../../constants/enums';
import InfoTooltip from '../InfoTooltip/InfoTooltip';
import DisabledFeature from '../DisabledFeature/DisabledFeature';
import UploadTestFiles from '../UploadTestFiles/UploadTestFiles';
import DropdownSelect from '../DropdownSelect/DropdownSelect';
import ButtonModalTestSuggestionAI from '../ButtonModalTestSuggestionAI/ButtonModalTestSuggestionAI';
import { useQuestionHandlers } from '../../hooks/useQuestionHandlers';
import NewStyleSelect from '../NewStyleSelect/NewStyleSelect';
import { maxRecordTimes } from '../../constants/questions';
import { isAiInterviewStrategy } from '../../utils/testgroups';
import { getAvailablePromptsByStrategy } from '../../utils/prompts';

const QuestionText = ({ readOnly = false, isTranslateABM = false, testGroupABM = false }) => {
  const {
    testAnswer,
    setTestAnswer,
    aiImg,
    handleAddQuestionText,
    handleCurrentTypeQuestion,
    handleGenerateAIAnswer,
    loadingAIAnswer,
    currentQuestionData,
    currentTypeQuestion,
    handleAddTestFile,
    handleRemoveTestFile,
    handleUpdateTestFile,
    promptsCorrectionTextAI,
    handleAddPrompTextAI,
    customTestGroupStrategy,
  } = useQuestionHandlers(isTranslateABM);

  const [promptsCorrectionTextAIOptions, setPromptsCorrectionTextAIOptions] = useState([]);

  useEffect(() => {
    const options = promptsCorrectionTextAI
      ?.filter((prompt) =>
        getAvailablePromptsByStrategy(customTestGroupStrategy, prompt.promptSubType)
      )
      ?.map((item) => ({
        id: t(item.promptSubType),
        name: t(item.promptSubType),
        promptId: item.promptSubType,
        promptSubType: item.promptSubType,
      }));
    setPromptsCorrectionTextAIOptions(options);
  }, [promptsCorrectionTextAI, customTestGroupStrategy]);

  return (
    <div data-testid="question-text">
      {!isTranslateABM && !readOnly && <ButtonModalTestSuggestionAI />}
      <div className="row">
        <div className="flex-column col-6">
          <textarea
            name="text"
            className={`col-10 border-grey-2 outline-none p-2 px-3 text-xs font-weight-500  ${
              currentQuestionData?.text?.length ? 'selected-border' : 'unselected-border'
            }`}
            rows="5"
            placeholder={t('CREATE_SCREENING_YOUR_QUESTIONS_WRITE_YOU_QUESTION_PLACEHOLDER')}
            onChange={(e) => handleAddQuestionText(e)}
            value={currentQuestionData?.text ?? ''}
            maxLength={5000}
            data-testid="question-text-textarea"
            disabled={readOnly}
          />
          {testGroupABM && (
            <div>
              <textarea
                type="text"
                onChange={handleAddQuestionText}
                name="formula"
                value={currentQuestionData?.formula ?? ''}
                className={`col-10 mt-3 py-2 px-3  border-thin-blue-principal outline-none text-xs min-height-add-question ${
                  currentQuestionData?.formula?.length ? 'selected-border' : 'unselected-border'
                }`}
                rows="1"
                maxLength={500}
                placeholder={t('CREATE_SCREENING_YOUR_FORMULAS_PLACEHOLDER')}
                data-testid="question-mc-add-formula-textarea"
                disabled={readOnly}
              />
              {!readOnly && (
                <UploadTestFiles
                  fileTypes={[TestFileType.IMAGE, TestFileType.AUDIO]}
                  handleAddFile={handleAddTestFile}
                  handleRemoveFile={handleRemoveTestFile}
                  handleUpdateFile={handleUpdateTestFile}
                  testFiles={currentQuestionData?.testFiles ?? []}
                  id="test"
                />
              )}
            </div>
          )}

          {isAiInterviewStrategy(customTestGroupStrategy) ? (
            <div
              data-testId="question-video-answer-total-time"
              className="justify-content-start d-flex flex-column w-100"
            >
              <div>
                <span className=" font-italic text-muted font-weight-bold text-xs">
                  {t('CREATE_SCREENING_TOTAL_RECORD_TIME')}
                </span>
              </div>
              <div>
                <NewStyleSelect
                  error={false}
                  className="w-100"
                  value={
                    currentQuestionData?.totalTime ?? maxRecordTimes[maxRecordTimes?.length - 1].id
                  }
                  options={maxRecordTimes}
                  onChange={(e) => {
                    handleAddQuestionText(e);
                  }}
                  name="totalTime"
                  borderClasses="bottom-border text-center"
                  disabled={readOnly || isTranslateABM}
                />
              </div>
            </div>
          ) : (
            ([
              PromptSubTypes.CONCEPTS_CORRECTION,
              PromptSubTypes.SFIA_CORRECTION,
              PromptSubTypes.SHORT_ANSWER_CORRECTION,
            ].includes(currentQuestionData?.promptSubType) ||
              currentTypeQuestion === AnswerTypeEnum.TEXT) && (
              <div className="mt-3">
                <input
                  type="number"
                  name="maxChar"
                  value={currentQuestionData?.maxChar ?? ''}
                  onKeyDown={(e) => {
                    const excludedKeys = ['e', '+', '-', '.'];
                    if (excludedKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => handleAddQuestionText(e)}
                  className="pb-3 mt-4 bottom-border div-input w-content h-100 b-radius-8 max-char-input text-xs font-weight-400  text-center col-10"
                  min={0}
                  max={5000}
                  data-testid="question-text-max-char-input"
                  placeholder={t('CREATE_SCREENING_MAX_CHAR_ANSWER')}
                  disabled={readOnly || isTranslateABM}
                />
              </div>
            )
          )}
        </div>
        <div className="col-6 gap-1 ">
          {testGroupABM ? (
            <div className="d-flex align-items-center gap-1">
              <span className="ai-image-container">
                {aiImg}
                {t('QUESTION_TEXT_TOGGLE_AI_CORRECTION')}
              </span>
              <GenericToggle
                actionFunction={() => {
                  if (currentTypeQuestion !== AnswerTypeEnum.TEXT_AI) {
                    handleCurrentTypeQuestion(AnswerTypeEnum.TEXT_AI);
                  } else {
                    handleCurrentTypeQuestion(AnswerTypeEnum.TEXT);
                  }
                }}
                defaultActive={currentTypeQuestion === AnswerTypeEnum.TEXT_AI}
                disabled={readOnly}
              />
            </div>
          ) : (
            <DisabledFeature
              className="d-flex flex-row"
              iconClassName="align-self-start"
              childrenClassname="mt-2 pt-1"
              featureId={PlanFeaturesIds.PROCTORING}
            >
              <div className="d-flex align-items-center gap-1 ai-image-container">
                {aiImg}
                <span className=" text-xs font-weight-500">
                  {t('QUESTION_TEXT_TOGGLE_AI_CORRECTION')}
                </span>
                {!isAiInterviewStrategy(customTestGroupStrategy) && (
                  <GenericToggle
                    className="svgs-text-ai"
                    disabled={testGroupABM || readOnly}
                    actionFunction={() => {
                      if (currentTypeQuestion !== AnswerTypeEnum.TEXT_AI) {
                        handleCurrentTypeQuestion(AnswerTypeEnum.TEXT_AI);
                      } else {
                        handleCurrentTypeQuestion(AnswerTypeEnum.TEXT);
                      }
                    }}
                    defaultActive={currentTypeQuestion === AnswerTypeEnum.TEXT_AI}
                  />
                )}
              </div>
            </DisabledFeature>
          )}
          {(currentTypeQuestion === AnswerTypeEnum.TEXT_AI ||
            isAiInterviewStrategy(customTestGroupStrategy)) && (
            <div className="d-flex flex-column align-items-start gap-1 mt-3">
              <div className="d-flex flex-column w-100">
                {promptsCorrectionTextAIOptions?.length > 0 && (
                  <div>
                    <DropdownSelect
                      options={promptsCorrectionTextAIOptions}
                      className="w-100"
                      selected={t(currentQuestionData?.promptSubType)}
                      selectedPlaceholder
                      placeholderClassname="mr-2 text-xs font-weight-500"
                      axHeight={false}
                      bigArrowLightBlue
                      arrow={false}
                      placeholder={t('PROMPTS_CHOOSE_CORRECTION_TEXT_AI')}
                      setState={handleAddPrompTextAI}
                      disabled={
                        readOnly || isTranslateABM || promptsCorrectionTextAIOptions?.length === 1
                      }
                      autoSelectUniqueOption
                    />
                  </div>
                )}
                {[
                  PromptSubTypes.CONCEPTS_CORRECTION,
                  PromptSubTypes.TRANSCRIPTION_CORRECTION,
                  PromptSubTypes.ORTHOGRAPHY_CORRECTION,
                  PromptSubTypes.SFIA_CORRECTION,
                  PromptSubTypes.SHORT_ANSWER_CORRECTION,
                ].includes(currentQuestionData?.promptSubType) && (
                  <>
                    <span className=" text-xs font-weight-400 min-height-ia-text d-flex align-items-start">
                      {t(`QUESTION_TEXT_TOGGLE_AI_ADD_${currentQuestionData?.promptSubType}`)}
                      <InfoTooltip
                        className="ml-2 info-tooltip-ai"
                        placement="top"
                        content={
                          <div className="d-flex flex-column py-2">
                            <span className=" text-xs mb-2">
                              {t(
                                `QUESTION_TEXT_TOGGLE_AI_ADD_${currentQuestionData?.promptSubType}_FIRST_DISCLAIMER`
                              )}
                            </span>
                            <span className=" text-xs">
                              {t(
                                `QUESTION_TEXT_TOGGLE_AI_ADD_${currentQuestionData?.promptSubType}_SECOND_DISCLAIMER`
                              )}
                            </span>
                          </div>
                        }
                      />
                    </span>
                    <textarea
                      name="concepts"
                      className={`bottom-border outline-none w-100 mt-3 p-2 px-3 text-xs font-weight-400  ${
                        currentQuestionData?.concepts?.length
                          ? 'selected-border'
                          : 'unselected-border'
                      }`}
                      rows="3"
                      onChange={(e) => handleAddQuestionText(e)}
                      value={currentQuestionData?.concepts ?? ''}
                      maxLength={1000}
                      data-testid="question-text-concepts-textarea"
                      disabled={readOnly || isTranslateABM}
                    />
                  </>
                )}
              </div>
              <div className="d-flex flex-column w-100">
                <span className=" text-xs font-weight-400 min-height-ia-text d-flex align-items-start">
                  {t('QUESTION_TEXT_TOGGLE_AI_ADD_TEST_ANSWER')}
                  <InfoTooltip
                    className="ml-2 info-tooltip-ai"
                    placement="top"
                    content={
                      <div className="d-flex flex-column py-2">
                        <span className=" text-xs mb-2">
                          {t(
                            `QUESTION_TEXT_TOGGLE_AI_ADD_TEST_ANSWER_DISCLAIMER_${currentQuestionData?.promptSubType}`
                          ).replace('{PPM}', MECHANOGRAFY_PPM_TEST)}
                        </span>
                      </div>
                    }
                  />
                </span>
                <textarea
                  name="testAnswer"
                  className={`bottom-border outline-none w-100 mt-3 p-2 px-3 text-xs font-weight-400  ${
                    testAnswer?.text?.length ? 'selected-border' : 'unselected-border'
                  }`}
                  rows="3"
                  onChange={(e) => setTestAnswer({ text: e.target.value })}
                  value={testAnswer?.text ?? ''}
                  maxLength={5000}
                  data-testid="question-text-test-answer-textarea"
                  disabled={readOnly}
                />
                <div className="d-flex mt-2 align-items-center justify-content-between text-xs font-weight-400">
                  <div className="d-flex flex-column gap-2">
                    <span className="bg-grey-3 p-2">
                      {t('QUESTION_TEXT_TOGGLE_AI_ADD_TEST_ANSWER_SCORE')}
                      {testAnswer?.score && `${testAnswer?.score * 10} %`}
                    </span>
                    <span className="bg-grey-3 p-2">
                      {t('QUESTION_TEXT_TOGGLE_AI_ADD_TEST_ANSWER_EXPLANATION')}
                      {testAnswer?.explanation}
                    </span>
                  </div>
                  <div className="d-flex align-items-center">
                    <button
                      data-testid="question-text-add-test-answer-button"
                      onClick={async () => {
                        setTestAnswer({
                          ...testAnswer,
                          score: undefined,
                          explanation: undefined,
                        });
                        const data = await handleGenerateAIAnswer(testAnswer.text);
                        setTestAnswer({
                          ...testAnswer,
                          score: data.score,
                          explanation: data.explanation,
                        });
                      }}
                      type="button"
                      disabled={
                        !testAnswer?.text?.length ||
                        (!currentQuestionData?.concepts?.length &&
                          [
                            PromptSubTypes.CONCEPTS_CORRECTION,
                            PromptSubTypes.TRANSCRIPTION_CORRECTION,
                            PromptSubTypes.ORTHOGRAPHY_CORRECTION,
                            PromptSubTypes.SFIA_CORRECTION,
                            PromptSubTypes.SHORT_ANSWER_CORRECTION,
                          ].includes(currentQuestionData?.promptSubType)) ||
                        !currentQuestionData?.text?.length ||
                        loadingAIAnswer ||
                        readOnly
                      }
                      className={`members-invite-submit-button  text-xs align-self-start font-weight-bold py-2 px-4 rounded-pill text-white
                  ${
                    !testAnswer?.text?.length ||
                    (!currentQuestionData?.concepts?.length &&
                      [
                        PromptSubTypes.CONCEPTS_CORRECTION,
                        PromptSubTypes.TRANSCRIPTION_CORRECTION,
                        PromptSubTypes.ORTHOGRAPHY_CORRECTION,
                        PromptSubTypes.SHORT_ANSWER_CORRECTION,
                        PromptSubTypes.SFIA_CORRECTION,
                      ].includes(currentQuestionData?.promptSubType)) ||
                    !currentQuestionData?.text?.length ||
                    loadingAIAnswer
                      ? 'disabled'
                      : ''
                  }
                  `}
                    >
                      {t('QUESTION_TEXT_TOGGLE_AI_ADD_TEST_ANSWER_BUTTON')}
                    </button>
                    <InfoTooltip
                      className="ml-2"
                      placement="top"
                      content={
                        <div className="d-flex flex-column py-2">
                          <span className=" text-xs mb-2">
                            {t('QUESTION_TEXT_TOGGLE_AI_ADD_TEST_ANSWER_BUTTON_FIRST_DISCLAIMER')}
                          </span>
                          <span className=" text-xs">
                            {t('QUESTION_TEXT_TOGGLE_AI_ADD_TEST_ANSWER_BUTTON_SECOND_DISCLAIMER')}
                          </span>
                        </div>
                      }
                    />
                  </div>
                </div>
                {loadingAIAnswer && (
                  <div className="w-100 d-flex justify-content-center mt-3">
                    <HorizontalLoader />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionText;
